/* sugar:assets/video-controls.js@2.1.2 */
/* Video controls engine.
 *
 * A controls overlay (.video-controls, rendered by the Custom/Simple Video
 * Controls blocks) binds to the video(s) of whichever host it was dropped
 * into:
 *
 * - Single-media host (.media-block__wrapper): the overlay stays where it
 *   rendered and binds to the wrapper's one video. Same contract as the
 *   original inline scripts.
 * - Social Media Videos ([data-smv]): the rendered overlay is a hidden source
 *   in the video list; a clone goes into the popup's [data-vc-slot] and binds
 *   to the one popup video every clip plays in. The block fires `smv:popup`
 *   once its popup has settled (it moves to <body>), which re-runs the scan.
 * - Multi-video host ([data-pmg-host], the product gallery family): the
 *   rendered overlay is a hidden source; a clone is placed inside every
 *   [data-vc-slot] (one per video slide, Splide loop clones included) and
 *   each clone binds to its slot's video. A `gallery:init` event on the
 *   gallery re-runs the scan, which also covers slides rebuilt by variant
 *   filtering.
 *
 * Bindings are idempotent per video (a fresh clone of a slide carries no JS
 * properties, so rebuilt slides rebind cleanly).
 */
window.SugarVideoControls = (function () {
  'use strict';

  function bindVideo(c, v, w) {
    var pb = c.querySelector('.media-block__play-button');
    if (pb || v.autoplay) v.controls = false;

    if (pb) {
      // A button that carries a pause glyph (Show pause button while playing) stays on screen
      // and swaps glyphs with the video's state, and a press while playing pauses; otherwise
      // the button hides while the video plays.
      var playFace = pb.querySelectorAll('.vc-play-face');
      var pauseFace = pb.querySelectorAll('.vc-pause-face');
      var swap = pauseFace.length > 0;
      var upd = function () {
        if (swap) {
          var playing = !v.paused;
          playFace.forEach(function (el) {
            el.style.display = playing ? 'none' : '';
          });
          pauseFace.forEach(function (el) {
            el.style.display = playing ? '' : 'none';
          });
          pb.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
          return;
        }
        pb.style.opacity = v.paused ? '' : '0';
        pb.style.pointerEvents = v.paused ? '' : 'none';
      };
      pb.addEventListener('click', function () {
        if (swap && !v.paused) v.pause();
        else v.play();
      });
      v.addEventListener('click', function () {
        if (v.paused) v.play();
        else v.pause();
      });
      v.addEventListener('play', upd);
      v.addEventListener('pause', upd);
      if (v.autoplay) v.addEventListener('playing', upd, { once: true });
      // A video that started before the engine bound shows its pause glyph straight away
      if (swap) upd();

      // A pause button in the middle of the picture steps aside while the video plays: it shows
      // while the mouse moves over the video, for a moment after a tap or a click, while it has
      // keyboard focus, and whenever the video is paused. A corner button stays put.
      if (swap && (c.getAttribute('data-play-pos') || 'center') === 'center') {
        var idleTimer = null;
        var rest = function () {
          if (!v.paused && !pb.matches(':focus-visible')) c.setAttribute('data-vc-idle', '');
        };
        var wake = function (ms) {
          clearTimeout(idleTimer);
          c.removeAttribute('data-vc-idle');
          if (!v.paused) idleTimer = setTimeout(rest, ms);
        };
        v.addEventListener('play', function () {
          wake(1500);
        });
        v.addEventListener('pause', function () {
          wake(0);
        });
        w.addEventListener('pointermove', function (e) {
          if (e.pointerType === 'mouse') wake(1500);
        });
        w.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse') wake(2000);
        });
        w.addEventListener('pointerleave', function (e) {
          if (e.pointerType !== 'mouse' || v.paused) return;
          clearTimeout(idleTimer);
          c.setAttribute('data-vc-idle', '');
        });
        pb.addEventListener('focus', function () {
          wake(1500);
        });
        if (!v.paused) wake(1500);
      }
    }

    var tl = c.querySelector('.media-block__timeline-progress');
    if (tl) {
      var paint = function () {
        if (!v.duration) return;
        var r = v.currentTime / v.duration;
        tl.style.transform = 'scaleX(' + r + ')';
        // The control bar's pill clips its fill with this instead, so the rounded ends never squash
        tl.style.setProperty('--vc-p', r);
      };
      v.addEventListener('timeupdate', paint);

      // The bar seeks: press anywhere on it to jump there, drag to scrub. The pointer is
      // captured so a scrub can wander off the bar, and the touch never reaches the slider
      // the video sits in, which would otherwise drag the slide instead.
      var bar = tl.parentElement;
      var seekTo = function (e) {
        var r = bar.getBoundingClientRect();
        if (!r.width || !v.duration) return;
        var x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
        v.currentTime = (x / r.width) * v.duration;
        paint();
      };
      var scrubbing = false;
      bar.addEventListener('pointerdown', function (e) {
        if (e.button) return;
        e.preventDefault();
        e.stopPropagation();
        scrubbing = true;
        if (bar.setPointerCapture) bar.setPointerCapture(e.pointerId);
        seekTo(e);
      });
      bar.addEventListener('pointermove', function (e) {
        if (scrubbing) seekTo(e);
      });
      var done = function () {
        scrubbing = false;
      };
      bar.addEventListener('pointerup', done);
      bar.addEventListener('pointercancel', done);
      bar.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });
      bar.addEventListener('touchmove', function (e) { e.stopPropagation(); e.preventDefault(); }, { passive: false });
      bar.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    var rb = c.querySelector('.media-block__replay-button');
    if (rb) {
      rb.addEventListener('click', function (e) {
        e.stopPropagation();
        v.currentTime = 0;
        v.play();
      });
    }

    var fb = c.querySelector('.media-block__fullscreen-button');
    if (fb) {
      var fse = fb.querySelector('.fs-icon--enter');
      var fsx = fb.querySelector('.fs-icon--exit');
      var useVideoFs = !!(v.webkitEnterFullscreen && !w.requestFullscreen && !w.webkitRequestFullscreen);
      var fsIsActive = function () {
        if (useVideoFs) return v.webkitDisplayingFullscreen;
        return (document.fullscreenElement || document.webkitFullscreenElement) === w;
      };
      var fsExit = function () {
        if (useVideoFs) {
          v.webkitExitFullscreen();
          return;
        }
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      };
      var fsSize = function () {
        if (!fsIsActive() || !v.videoWidth) return;
        var sw = w.clientWidth;
        var sh = w.clientHeight;
        var vr = v.videoWidth / v.videoHeight;
        if (vr > sw / sh) {
          v.style.width = sw + 'px';
          v.style.height = sw / vr + 'px';
        } else {
          v.style.height = sh + 'px';
          v.style.width = sh * vr + 'px';
        }
      };
      var fsu = function () {
        var isFs = fsIsActive();
        fse.style.display = isFs ? 'none' : '';
        fsx.style.display = isFs ? '' : 'none';
        if (isFs) fsSize();
        else {
          v.style.width = '';
          v.style.height = '';
        }
      };
      fb.addEventListener('click', function (e) {
        e.stopPropagation();
        if (fsIsActive()) fsExit();
        else if (useVideoFs) v.webkitEnterFullscreen();
        else (w.requestFullscreen || w.webkitRequestFullscreen).call(w);
      });
      w.addEventListener('click', function (e) {
        if (e.target === w && fsIsActive()) fsExit();
      });
      window.addEventListener('resize', fsSize);
      document.addEventListener('fullscreenchange', fsu);
      document.addEventListener('webkitfullscreenchange', fsu);
      if (useVideoFs) {
        v.addEventListener('webkitbeginfullscreen', fsu);
        v.addEventListener('webkitendfullscreen', fsu);
      }
    }

    var sb = c.querySelector('.media-block__sound-button');
    if (sb) {
      var me = sb.querySelector('.sound-icon--muted') || sb.querySelector('.sound-text--muted');
      var ue = sb.querySelector('.sound-icon--unmuted') || sb.querySelector('.sound-text--unmuted');
      var su = function () {
        me.style.display = v.muted ? '' : 'none';
        ue.style.display = v.muted ? 'none' : '';
      };
      sb.addEventListener('click', function (e) {
        e.stopPropagation();
        v.muted = !v.muted;
        su();
      });
      // A host that mutes or unmutes the video itself (a popup unmuting after play) keeps the icon true
      v.addEventListener('volumechange', su);
      su();
    }
  }

  function scanHost(host, overlay) {
    host.querySelectorAll('[data-vc-slot]').forEach(function (slot) {
      var v = slot.querySelector('video');
      if (!v) return;
      var clone = slot.querySelector('.video-controls');
      if (!clone) {
        clone = overlay.cloneNode(true);
        slot.appendChild(clone);
      }
      if (v.__vcBound) return;
      v.__vcBound = true;
      bindVideo(clone, v, slot);
    });
  }

  function attach(overlay) {
    if (!overlay) return;

    var single = overlay.closest('.media-block__wrapper');
    if (single) {
      var v = single.querySelector('.media-block__video');
      if (!v || v.__vcBound) return;
      v.__vcBound = true;
      bindVideo(overlay, v, single);
      return;
    }

    var smv = overlay.closest('[data-smv]');
    if (smv) {
      var id = smv.getAttribute('data-smv');
      var scanPopup = function () {
        // An editor re-render replaces the overlay; the stale one stops answering
        if (!overlay.isConnected) return;
        document.querySelectorAll('[data-popup-modal="' + id + '"]').forEach(function (popup) {
          scanHost(popup, overlay);
        });
      };
      scanPopup();
      document.addEventListener('smv:popup', function (e) {
        if (e.detail && e.detail.id === id) scanPopup();
      });
      return;
    }

    var host = overlay.closest('[data-pmg-host]');
    if (!host) return;

    var scan = function () {
      scanHost(host, overlay);
    };
    scan();

    var main = host.querySelector('.product-media-gallery__main');
    if (main && !main.__vcRescan) {
      main.__vcRescan = true;
      main.addEventListener('gallery:init', scan);
    }
  }

  return { attach: attach, bindVideo: bindVideo };
})();
